/**
 * SaludPanel — GEN-MO-018: el Owner ve si su instalación está sana o degradada, sin que nadie la mida
 * desde fuera. Solo admin: el ítem `salud` lo proyecta el kernel y la puerta real es GET /v1/admin/salud
 * (403 al resto). El kernel decide el estado de cada componente y el global; aquí solo se pintan.
 * Los componentes salen de la respuesta: uno nuevo aparece sin tocar este fichero.
 *
 * Del índice semántico se muestra la fecha del último indexado y, si hay iKus pendientes, cuánto llevan
 * esperando frente al plazo. GEN-PX-017: debajo va la salud del índice de búsqueda con sus acciones (reconstruir y quitar
 * huérfanos), que antes estaban al pie de KBs; al terminar una, se vuelve a comprobar la salud. La licencia aparece «no gestionada» hasta que exista el servidor de licencias (GEN-208).
 */

import { useCallback, useEffect, useState } from 'react'
import { getSalud } from '../api/client.js'
import IndicePanel from './IndicePanel.jsx'

const TEXTO_ESTADO = {
  ok: 'Correcto',
  degradado: 'Degradado',
  no_disponible: 'No disponible',
  no_gestionada: 'No gestionada',
}

const cuando = iso => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('es-ES')
}

export function duracion(segundos) {
  const s = Math.max(0, Math.round(Number(segundos) || 0))
  if (s < 60) return `${s} s`
  if (s < 3600) return `${Math.floor(s / 60)} min`
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return m ? `${h} h ${m} min` : `${h} h`
}

function DetalleIndice({ d }) {
  if (!d) return null
  const ultimo = cuando(d.ultimo_proyectado)
  return (
    <ul className="salud-detalle">
      <li>Último indexado: {ultimo ?? 'nunca'}</li>
      {d.pendientes > 0 && d.retraso_max_s != null && (
        <li>
          {d.pendientes} iKu{d.pendientes === 1 ? '' : 's'} pendiente{d.pendientes === 1 ? '' : 's'} de indexar;
          el más antiguo lleva {duracion(d.retraso_max_s)} (plazo {duracion(d.plazo_s)})
        </li>
      )}
      {d.ultima_reconstruccion && <li>Última reconstrucción: {cuando(d.ultima_reconstruccion)}</li>}
    </ul>
  )
}

export default function SaludPanel() {
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState(null)

  const cargar = useCallback(async () => {
    try {
      setDatos(await getSalud())
      setError(null)
    } catch (e) {
      setError(e?.code === 'forbidden' ? 'Solo el administrador ve la salud de la instalación.'
        : 'No se ha podido comprobar la salud de la instalación.')
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  if (!datos && !error) return <section className="mec-visor"><h2>Salud</h2><p>Cargando…</p></section>
  if (error) {
    return <section className="mec-visor"><h2>Salud</h2><div className="mec-error" role="alert">{error}</div></section>
  }

  const sana = datos.estado === 'ok'
  const indice = datos.componentes.find(c => c.id === 'indice')

  return (
    <section className="mec-visor salud">
      <h2>Salud</h2>
      <p className={`salud-global salud-global--${sana ? 'ok' : 'degradado'}`} role="status">
        <strong>{sana ? 'Instalación sana' : 'Instalación degradada'}</strong>
        {datos.comprobado_en && <span className="coste-marca">comprobado {cuando(datos.comprobado_en)}</span>}
      </p>

      <table className="mec-tabla">
        <thead>
          <tr><th>Componente</th><th>Estado</th><th>Detalle</th></tr>
        </thead>
        <tbody>
          {datos.componentes.map(c => (
            <tr key={c.id} data-testid={`salud-${c.id}`}>
              <td>{c.nombre}</td>
              <td><span className={`salud-estado salud-estado--${c.estado}`}>{TEXTO_ESTADO[c.estado] ?? c.estado}</span></td>
              <td>
                {c.motivo}
                {c.id === 'indice' && <DetalleIndice d={c.detalle} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {indice && indice.estado !== 'no_disponible' && <IndicePanel onReconstruido={cargar} />}
      <p className="coste-nota">
        La salud se comprueba al abrir esta vista; nadie la mide desde fuera. Un iKu pendiente de indexar más allá del
        plazo pone el índice en «degradado».
      </p>
    </section>
  )
}
